import { useState, useRef, useMemo, useCallback } from "react";

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const META = { unit: "ms", min: 0, max: 150, gridStep: 30, color: "#f59e0b", low: 30, high: null };

function formatClock(ms) {
  if (!ms) return "--";
  return new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function getStatus(value) {
  if (value == null || Number.isNaN(value)) return { label: "--", tone: "neutral" };
  if (value >= 50) return { label: "Good", tone: "good" };
  if (value >= 30) return { label: "Fair", tone: "warn" };
  return { label: "Low", tone: "bad" };
}

const TONE_STYLES = {
  good:    { color: "#16a34a", bg: "#dcfce7" },
  warn:    { color: "#d97706", bg: "#fef3c7" },
  bad:     { color: "#dc2626", bg: "#fee2e2" },
  neutral: { color: "#6b7280", bg: "#f1f5f9" },
};

function StatusChip({ tone, label }) {
  const s = TONE_STYLES[tone] || TONE_STYLES.neutral;
  return <span className="status-chip" style={{ color: s.color, background: s.bg }}>{label}</span>;
}

export default function HrvGraph({ bucketData, selectedDate, sleepDayData, dateControl }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [mode, setMode] = useState("day");

  const dayStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [selectedDate]);

  const sleepWindow = useMemo(() => {
    if (!sleepDayData) return null;
    const start = Math.max(sleepDayData.sleepStart, dayStart);
    const end = Math.min(sleepDayData.sleepEnd, dayStart + DAY_MS);
    if (end <= start) return null;
    return { start, end };
  }, [sleepDayData, dayStart]);

  const view = useMemo(() => {
    if (mode === "night" && sleepWindow) {
      const pad = 30 * 60 * 1000;
      return { start: Math.max(dayStart, sleepWindow.start - pad), end: Math.min(dayStart + DAY_MS, sleepWindow.end + pad) };
    }
    return { start: dayStart, end: dayStart + DAY_MS };
  }, [mode, sleepWindow, dayStart]);

  const viewRange = view.end - view.start;

  const points = useMemo(
    () => bucketData.filter(b => b.time >= view.start && b.time <= view.end && b.value != null),
    [bucketData, view]
  );

  const values = points.map(p => p.value);
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : META.max;
  const yMin = Math.min(META.min, dataMin);
  const yMax = Math.max(META.max, dataMax);
  const yRange = (yMax - yMin) || 1;

  const W = 1000, H = 220, PAD_L = 34, PAD_R = 10, PAD_T = 10, PAD_B = 24;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const xForTime = useCallback(t => PAD_L + ((t - view.start) / viewRange) * plotW, [view, viewRange, plotW]);
  const yForValue = useCallback(v => PAD_T + plotH - ((v - yMin) / yRange) * plotH, [yMin, yRange, plotH]);

  const dotPositions = useMemo(
    () => points.map(p => ({ x: xForTime(p.time), y: yForValue(p.value), time: p.time, value: p.value })),
    [points, xForTime, yForValue]
  );

  const polylinePoints = dotPositions.map(p => `${p.x},${p.y}`).join(" ");

  const gridStep = META.gridStep;
  const gridLines = [];
  for (let v = Math.ceil(yMin / gridStep) * gridStep; v <= yMax; v += gridStep) gridLines.push(v);

  const xTicks = useMemo(() => {
    return HOUR_TICKS.map(h => dayStart + h * 60 * 60 * 1000).filter(t => t >= view.start && t <= view.end);
  }, [dayStart, view]);

  const events = useMemo(() => {
    const list = [];
    if (sleepWindow) list.push({ time: sleepWindow.end, label: "Sleep End", glyph: "🌙" });
    return list.filter(e => e.time >= view.start && e.time <= view.end);
  }, [sleepWindow, view]);

  const hovered = hoverIdx != null ? dotPositions[hoverIdx] : null;
  const hoveredStatus = hovered ? getStatus(hovered.value) : null;

  const handleMove = useCallback(e => {
    if (!dotPositions.length) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0, best = Infinity;
    dotPositions.forEach((p, i) => { const d = Math.abs(p.x - relX); if (d < best) { best = d; nearest = i; } });
    setHoverIdx(nearest);
  }, [dotPositions]);

  const handleLeave = useCallback(() => setHoverIdx(null), []);

  const latest = dotPositions.length ? dotPositions[dotPositions.length - 1] : null;
  const latestStatus = latest ? getStatus(latest.value) : null;

  return (
    <div className="allgraph-container">
      <div className="graph-date-bar">{dateControl}</div>

      <div className="vital-chart-header">
        <div className="vital-chart-latest">
          {latest ? (
            <>
              <div className="vital-chart-latest-time">Latest: {formatClock(latest.time)}</div>
              <div className="vital-chart-latest-value">
                {Math.round(latest.value).toLocaleString("en-IN")}
                <span className="vital-chart-latest-unit"> {META.unit}</span>
              </div>
              <StatusChip tone={latestStatus.tone} label={latestStatus.label} />
            </>
          ) : (
            <div className="vital-chart-latest-time">No data for this day</div>
          )}
        </div>

        <div className="day-night-toggle">
          <button className={mode === "day" ? "active" : ""} onClick={() => setMode("day")}>☀️ Day</button>
          <button
            className={mode === "night" ? "active" : ""}
            disabled={!sleepWindow}
            title={sleepWindow ? "Zoom into sleep window" : "No sleep data for this day"}
            onClick={() => sleepWindow && setMode("night")}
          >🌙 Night</button>
        </div>
      </div>

      <div className="allgraph-chart vital-chart-svg-wrap" ref={wrapRef} onMouseMove={handleMove} onMouseLeave={handleLeave} style={{ height: 260 }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
          {gridLines.map(v => {
            const y = yForValue(v);
            const isThreshold = v === META.low || v === META.high;
            return (
              <g key={v}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                  stroke={isThreshold ? (v === META.high ? "#f97316" : "#f59e0b") : "#e5e7eb"}
                  strokeDasharray={isThreshold ? "4 3" : "3 3"} strokeWidth={1} />
                <text x={2} y={y + 3} fontSize="9" fill="#9ca3af">{v}</text>
              </g>
            );
          })}

          {xTicks.map(t => (
            <text key={t} x={xForTime(t)} y={H - 6} fontSize="9" fill="#9ca3af" textAnchor="middle">
              {new Date(t).toLocaleTimeString("en-IN", { hour: "numeric", hour12: true }).replace(" ", "")}
            </text>
          ))}

          {polylinePoints && (
            <polyline points={polylinePoints} fill="none" stroke={META.color} strokeOpacity={0.35} strokeWidth={1.5} />
          )}
          {dotPositions.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={hoverIdx === i ? 3.5 : 2} fill={META.color} />
          ))}

          {hovered && (
            <line x1={hovered.x} x2={hovered.x} y1={PAD_T} y2={PAD_T + plotH} stroke="#94a3b8" strokeWidth={1} />
          )}
        </svg>

        {hovered && (
          <div className="vital-tooltip" style={{ left: `${(hovered.x / W) * 100}%`, top: `${(hovered.y / H) * 100}%`, borderColor: META.color }}>
            <div>{formatClock(hovered.time)}</div>
            <div style={{ color: META.color }}>HRV: {Math.round(hovered.value).toLocaleString("en-IN")} {META.unit}</div>
            <div>{hoveredStatus.label}</div>
          </div>
        )}
      </div>
    </div>
  );
}